import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, Details, Dialog, IconButton, List, ListItem, Select, Skeleton, StatusChip, TextField,
  TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, type components } from '../lib/api';
import { quandoRelativo } from '../lib/attivita';
import { BarraUtente } from '../shell';
import { Back, Copy, Plus, Refresh, Trasloco } from '../icons';
import { Anello, CaricoSezione } from '../brand';
import { t } from '../lib/lingua';
import { peso } from './backup-copertura';
import { NOME_FASE, moduliInstallati, nomeDellaCartella } from './trasloco';
import { useServers } from '../lib/dati';
import { MACCHINA_VUOTA, ScegliMacchina, useCredenzialiCloud, type MacchinaCloud } from './server-cloud';

type Sorgente = components['schemas']['Sorgente'];
type SorgenteCreata = components['schemas']['SorgenteCreata'];
type IstanzaSorgente = components['schemas']['IstanzaSorgente'];
type Trasloco = components['schemas']['Trasloco'];

/**
 * Il trasloco da CloudPepper, tutto da qui.
 *
 * Una macchina CloudPepper si collega come **sorgente**: una riga da root e
 * Molo ci entra con un utente suo, ne legge le istanze e — più avanti, dalla
 * scheda di ciascuna — fa la prova, il trasloco vero e spegne l'originale.
 * La macchina non si prepara come un server: resta di CloudPepper finché non
 * è vuota.
 */
export function TraslochiPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/traslochi' });
  const navigate = useNavigate();
  const [collega, setCollega] = useState(false);
  const sorgenti = useSorgenti(slug);
  const traslochi = useTraslochi(slug);

  return (
    <>
      <TopAppBar
        title={t('Traslochi')}
        actions={
          <>
            <Button variant="filled" icon={<Plus />} onClick={() => setCollega(true)}>
              {t('Collega una macchina CloudPepper')}
            </Button>
            <BarraUtente slug={slug} />
          </>
        }
      />
      <CaricoSezione attiva={sorgenti.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Da CloudPepper a Molo. Colleghi la macchina CloudPepper con una riga da root: Molo ne legge le istanze — versione, moduli, repository, librerie, domini, pesi — e da lì fa la prova, il trasloco vero e spegne l’originale. Sulla macchina non installa niente.')}
          </p>
          {sorgenti.error && <Banner tone="error" title={t('Non riesco a leggere le macchine')}>{sorgenti.error.message}</Banner>}

          {(traslochi.data ?? []).length > 0 && (
            <section className="md-stack">
              <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Traslochi')}</h2>
              <List>
                {(traslochi.data ?? []).map((tr) => (
                  <ListItem
                    key={tr.id}
                    interactive
                    icon={<Trasloco />}
                    headline={tr.name}
                    supporting={`${tr.sourceName} · ${tr.sourceInstance} → ${tr.serverName}`}
                    trailing={<StatusChip status={tr.phase} label={t(NOME_FASE[tr.phase]?.label ?? tr.phase)} tone={NOME_FASE[tr.phase]?.tone ?? 'neutral'} />}
                    onClick={() => navigate({ to: '/orgs/$slug/traslochi/$id', params: { slug, id: tr.id } })}
                  />
                ))}
              </List>
            </section>
          )}

          <section className="md-stack">
            <h2 className="md-title-medium" style={{ margin: 0 }}>{t('Macchine CloudPepper')}</h2>
            {sorgenti.isPending && <Skeleton height="6rem" />}
            {sorgenti.data && sorgenti.data.length === 0 && (
              <Card variant="filled">
                <div className="md-stack">
                  <p className="md-body-medium" style={{ margin: 0 }}>
                    {t('Nessuna macchina collegata. Si comincia da qui: una per ogni server CloudPepper da cui portare via le istanze.')}
                  </p>
                  <div className="md-stack__inline">
                    <Button variant="tonal" icon={<Plus />} onClick={() => setCollega(true)}>
                      {t('Collega una macchina CloudPepper')}
                    </Button>
                  </div>
                </div>
              </Card>
            )}
            {sorgenti.data && sorgenti.data.length > 0 && (
              <List>
                {sorgenti.data.map((s) => (
                  <ListItem
                    key={s.id}
                    interactive
                    icon={<Trasloco />}
                    headline={s.name}
                    supporting={riassunto(s)}
                    trailing={<StatoSorgente s={s} />}
                    onClick={() => navigate({ to: '/orgs/$slug/traslochi/sorgenti/$id', params: { slug, id: s.id } })}
                  />
                ))}
              </List>
            )}
          </section>
        </div>
      </div>
      <CollegaSorgente slug={slug} aperto={collega} chiudi={() => setCollega(false)} />
    </>
  );
}

function useSorgenti(slug: string) {
  return useQuery({
    queryKey: ['migration-sources', slug],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/migration-sources', { params: { path: { slug } } })).then((r) => r.sources ?? []),
    // Finché una macchina aspetta la sua riga la lista si aggiorna da sola:
    // chi l'ha incollata sta guardando qui.
    refetchInterval: (q) => ((q.state.data ?? []).some((s) => s.status === 'waiting') ? 4000 : false),
  });
}

export function useTraslochi(slug: string) {
  return useQuery({
    queryKey: ['migrations', slug],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/migrations', { params: { path: { slug } } })).then((r) => r.migrations ?? []),
    refetchInterval: (q) => ((q.state.data ?? []).some((tr) => tr.phase === 'trial' || tr.phase === 'cutover') ? 5000 : false),
  });
}

function riassunto(s: Sorgente): string {
  const parti = [s.sshPort === 22 ? s.host : `${s.host}:${s.sshPort}`];
  if (s.status === 'waiting') {
    parti.push(t('aspetto che la riga venga eseguita sulla macchina'));
  } else if (s.inventory) {
    const n = s.inventory.instances?.length ?? 0;
    parti.push(n === 1 ? t('1 istanza') : t('{n} istanze', { n }));
    if (s.inventoryAt) parti.push(t('letta {quando}', { quando: quandoRelativo(s.inventoryAt) }));
  }
  return parti.join(' · ');
}

function StatoSorgente({ s }: { s: Sorgente }) {
  if (s.status === 'connected') return <StatusChip status="connected" label={t('Collegata')} tone="success" />;
  const scaduta = s.commandExpiresAt ? new Date(s.commandExpiresAt).getTime() < Date.now() : true;
  return scaduta
    ? <StatusChip status="waiting" label={t('Riga scaduta')} tone="error" />
    : <StatusChip status="waiting" label={t('Aspetto la riga')} tone="running" />;
}

/**
 * Collegare una macchina: nome e indirizzo, poi la riga da eseguire come
 * root. Il dialogo resta aperto e aspetta: quando Molo entra lo dice, con
 * quante istanze ha trovato.
 */
function CollegaSorgente({ slug, aperto, chiudi }: { slug: string; aperto: boolean; chiudi: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [nome, setNome] = useState('');
  const [host, setHost] = useState('');
  const [porta, setPorta] = useState('');
  const [creata, setCreata] = useState<SorgenteCreata | null>(null);

  const crea = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/migration-sources', {
        params: { path: { slug } },
        body: { name: nome.trim(), host: host.trim(), ...(porta.trim() ? { sshPort: Number(porta) } : {}) },
      })),
    onSuccess: (s) => {
      setCreata(s);
      void qc.invalidateQueries({ queryKey: ['migration-sources', slug] });
    },
  });
  const stato = useQuery({
    queryKey: ['migration-source', slug, creata?.id],
    queryFn: () =>
      must(api.GET('/api/v1/orgs/{slug}/migration-sources/{id}', { params: { path: { slug, id: creata!.id } } })),
    enabled: !!creata,
    refetchInterval: (q) => (q.state.data?.status === 'connected' && q.state.data.inventory ? false : 3000),
  });

  const esci = () => {
    chiudi();
    setCreata(null);
    setNome('');
    setHost('');
    setPorta('');
    crea.reset();
  };
  const collegata = stato.data?.status === 'connected' && stato.data.inventory;

  return (
    <Dialog
      open={aperto}
      onOpenChange={(v) => !v && esci()}
      title={creata ? t('Collega {nome}', { nome: creata.name }) : t('Collega una macchina CloudPepper')}
      description={creata ? undefined : t('Molo ci entra con un utente suo, ne legge le istanze e non ci installa niente.')}
      actions={
        creata ? (
          <>
            <Button variant="text" onClick={esci}>{t('Chiudi')}</Button>
            {collegata && (
              <Button
                variant="filled"
                onClick={() => {
                  const id = creata.id;
                  esci();
                  navigate({ to: '/orgs/$slug/traslochi/sorgenti/$id', params: { slug, id } });
                }}
              >
                {t('Guarda le istanze')}
              </Button>
            )}
          </>
        ) : (
          <>
            <Button variant="text" onClick={esci}>{t('Annulla')}</Button>
            <Button
              variant="filled"
              icon={crea.isPending ? <Anello /> : undefined}
              disabled={crea.isPending || !nome.trim() || !host.trim()}
              onClick={() => crea.mutate()}
            >
              {t('Avanti')}
            </Button>
          </>
        )
      }
    >
      {!creata ? (
        <div className="md-stack">
          {crea.error && <Banner tone="error" title={t('Non l’ho collegata')}>{(crea.error as Error).message}</Banner>}
          <TextField
            label={t('Nome')}
            value={nome}
            onChange={(e) => setNome(e.target.value.toLowerCase())}
            placeholder="cp-produzione-1"
            help={t('Come la chiami qui. Minuscole, cifre e trattini.')}
          />
          <TextField
            label={t('Indirizzo')}
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="51.91.0.10"
            help={t('L’IP o il nome con cui Molo la raggiunge in SSH.')}
          />
          <TextField
            label={t('Porta SSH')}
            value={porta}
            onChange={(e) => setPorta(e.target.value.replace(/\D/g, ''))}
            placeholder="22"
          />
        </div>
      ) : (
        <Comando slug={slug} sorgente={stato.data ?? creata} comando={creata.bootstrapCommand} collegata={!!collegata} />
      )}
    </Dialog>
  );
}

/** La riga da eseguire, con l'attesa accanto: finché Molo non entra, si aspetta qui. */
function Comando({ slug, sorgente, comando: primo, collegata }: {
  slug: string; sorgente: Sorgente; comando: string; collegata: boolean;
}) {
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [comando, setComando] = useState(primo);
  const nuovo = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/migration-sources/{id}/command', {
        params: { path: { slug, id: sorgente.id } },
      })),
    onSuccess: (s) => {
      setComando(s.bootstrapCommand);
      void qc.invalidateQueries({ queryKey: ['migration-source', slug, sorgente.id] });
      void qc.invalidateQueries({ queryKey: ['migration-sources', slug] });
    },
  });
  const scaduta = sorgente.status === 'waiting' && (!sorgente.commandExpiresAt || new Date(sorgente.commandExpiresAt).getTime() < Date.now());

  if (collegata) {
    const n = sorgente.inventory?.instances?.length ?? 0;
    return (
      <Banner tone="success" title={t('Collegata')}>
        {n === 1
          ? t('Molo è entrato in {host} e ha trovato 1 istanza.', { host: sorgente.host })
          : t('Molo è entrato in {host} e ha trovato {n} istanze.', { host: sorgente.host, n })}
      </Banner>
    );
  }
  return (
    <div className="md-stack">
      <p className="md-body-medium" style={{ margin: 0 }}>
        {t('Entra come {root} su {host} e incolla questa riga entro quindici minuti. Crea l’utente {utente}, gli autorizza la chiave di Molo e nient’altro: la macchina resta di CloudPepper.', {
          root: <strong>root</strong>,
          host: <span className="md-mono">{sorgente.host}</span>,
          utente: <span className="md-mono">molo-trasloco</span>,
        })}
      </p>
      {comando ? (
        <pre className="md-log md-code">{comando}</pre>
      ) : (
        <Banner title={t('La riga non si mostra una seconda volta')}>{t('Chiedine una nuova qui sotto.')}</Banner>
      )}
      {nuovo.error && <Banner tone="error" title={t('Non ho un comando nuovo')}>{(nuovo.error as Error).message}</Banner>}
      <div className="md-stack__inline">
        {comando && !scaduta && (
          <Button
            variant="tonal"
            icon={<Copy />}
            onClick={() => {
              void navigator.clipboard?.writeText(comando);
              avvisa(t('Comando copiato'));
            }}
          >
            {t('Copia il comando')}
          </Button>
        )}
        {(scaduta || !comando) && (
          <Button variant="tonal" icon={nuovo.isPending ? <Anello /> : <Refresh />} disabled={nuovo.isPending} onClick={() => nuovo.mutate()}>
            {t('Comando nuovo')}
          </Button>
        )}
      </div>
      {!scaduta && (
        <div className="md-attesa">
          <Anello />
          <p className="md-body-small" style={{ margin: 0 }}>
            {sorgente.status === 'connected' ? t('Entrato: leggo le istanze…') : t('Aspetto che la riga venga eseguita…')}
          </p>
        </div>
      )}
    </div>
  );
}

/** Una macchina CloudPepper e quello che c'è sopra. */
export function SorgentePage() {
  const { slug, id } = useParams({ from: '/shell/orgs/$slug/traslochi/sorgenti/$id' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [scollega, setScollega] = useState(false);
  const [trasloca, setTrasloca] = useState<IstanzaSorgente | null>(null);
  const traslochi = useTraslochi(slug);

  const q = useQuery({
    queryKey: ['migration-source', slug, id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/migration-sources/{id}', { params: { path: { slug, id } } })),
    refetchInterval: (r) => (r.state.data?.status === 'waiting' ? 4000 : false),
  });
  const rileggi = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/migration-sources/{id}/inventory', { params: { path: { slug, id } } })),
    onSuccess: (r) => {
      avvisa(t('Rileggo la macchina'));
      if (r.jobId) navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
    },
  });
  const togli = useMutation({
    mutationFn: (dimentica: boolean) =>
      must(api.DELETE('/api/v1/orgs/{slug}/migration-sources/{id}', {
        params: { path: { slug, id }, query: { forget: dimentica } },
      })),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['migration-sources', slug] });
      setScollega(false);
      if (r.jobId) {
        navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: r.jobId } });
      } else {
        avvisa(t('Macchina dimenticata'));
        navigate({ to: '/orgs/$slug/traslochi', params: { slug } });
      }
    },
  });
  const s = q.data;
  const istanze = s?.inventory?.instances ?? [];

  return (
    <>
      <TopAppBar
        title={s?.name ?? t('Macchina CloudPepper')}
        subtitle={s ? riassunto(s) : undefined}
        leading={
          <IconButton label={t('Torna ai traslochi')} onClick={() => navigate({ to: '/orgs/$slug/traslochi', params: { slug } })}>
            <Back />
          </IconButton>
        }
        actions={
          <>
            {s?.status === 'connected' && (
              <Button variant="tonal" icon={rileggi.isPending ? <Anello /> : <Refresh />} disabled={rileggi.isPending} onClick={() => rileggi.mutate()}>
                {t('Rileggi')}
              </Button>
            )}
            {s && <Button variant="text" onClick={() => setScollega(true)}>{t('Scollega')}</Button>}
            <BarraUtente slug={slug} />
          </>
        }
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          {q.error && <Banner tone="error" title={t('Non riesco a leggere la macchina')}>{q.error.message}</Banner>}
          {rileggi.error && <Banner tone="error" title={t('Non l’ho riletta')}>{(rileggi.error as Error).message}</Banner>}
          {q.isPending && <Skeleton height="12rem" />}
          {s?.status === 'waiting' && (
            <Card variant="outlined">
              <Comando slug={slug} sorgente={s} comando="" collegata={false} />
            </Card>
          )}
          {s?.inventory && (
            <>
              <p className="md-body-medium" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
                {s.inventory.hostname} · {s.inventory.os} · Python {s.inventory.python}
              </p>
              {(s.inventory.warnings ?? []).length > 0 && (
                <Banner title={t('Cose che non ho potuto leggere')}>{(s.inventory.warnings ?? []).join(' · ')}</Banner>
              )}
              {istanze.length === 0 && (
                <Card variant="filled"><p className="md-body-medium" style={{ margin: 0 }}>{t('Sulla macchina non c’è nessuna istanza di CloudPepper.')}</p></Card>
              )}
              {istanze.map((i) => (
                <SchedaIstanza
                  key={i.name}
                  i={i}
                  aperto={(traslochi.data ?? []).find((tr) => tr.sourceId === id && tr.sourceInstance === i.name && tr.phase !== 'cancelled')}
                  trasloca={() => setTrasloca(i)}
                  apri={(tr) => navigate({ to: '/orgs/$slug/traslochi/$id', params: { slug, id: tr } })}
                />
              ))}
            </>
          )}
        </div>
      </div>
      <NuovoTrasloco slug={slug} sorgente={id} istanza={trasloca} chiudi={() => setTrasloca(null)} />
      <Dialog
        open={scollega}
        onOpenChange={(v) => !v && setScollega(false)}
        title={t('Scollega {nome}', { nome: s?.name ?? '' })}
        description={t('Molo toglie dalla macchina l’utente molo-trasloco e la sua chiave. CloudPepper e le istanze restano come sono.')}
        actions={
          <>
            <Button variant="text" onClick={() => setScollega(false)}>{t('Annulla')}</Button>
            <Button variant="text" disabled={togli.isPending} onClick={() => togli.mutate(true)}>{t('Dimenticala')}</Button>
            <Button variant="filled" icon={togli.isPending ? <Anello /> : undefined} disabled={togli.isPending} onClick={() => togli.mutate(false)}>
              {t('Scollega')}
            </Button>
          </>
        }
      >
        {togli.error && <Banner tone="error" title={t('Non l’ho scollegata')}>{(togli.error as Error).message}</Banner>}
        <p className="md-body-small" style={{ margin: 0 }}>
          {t('«Dimenticala» è per una macchina che non c’è più: Molo non la tocca e toglie solo la sua chiave.')}
        </p>
      </Dialog>
    </>
  );
}

/** Un'istanza CloudPepper come la vede l'inventario, con quello che la ferma. */
function SchedaIstanza({ i, aperto, trasloca, apri }: {
  i: IstanzaSorgente;
  aperto?: Trasloco;
  trasloca: () => void;
  apri: (id: string) => void;
}) {
  const blocchi = i.blocks ?? [];
  const repos = i.repos ?? [];
  const cartelle = i.manual ?? [];
  const librerie = i.libraries ?? [];
  const domini = i.domains ?? [];
  return (
    <Card variant="outlined">
      <div className="md-stack">
        <div className="md-section-head">
          <h2 className="md-title-medium" style={{ margin: 0 }}>
            {i.name} <span className="md-body-medium" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
              · Odoo {i.major || '?'}{i.enterprise ? ' Enterprise' : ''}
            </span>
          </h2>
          {i.copy
            ? <StatusChip status="copy" label={t('Copia di prova')} tone="tertiary" />
            : <StatusChip status={i.active ? 'running' : 'stopped'} label={i.active ? t('Accesa') : t('Spenta')} tone={i.active ? 'success' : 'neutral'} />}
        </div>
        <p className="md-body-medium" style={{ margin: 0 }}>
          {t('Database {db} · allegati {allegati} · {moduli}', { db: peso(i.dbBytes), allegati: peso(i.filestoreBytes), moduli: moduliInstallati(i.modules) })}
          {domini.length > 0 && <> · {domini.join(', ')}</>}
        </p>
        {blocchi.length > 0 && (
          <Banner tone={i.copy ? 'info' : 'error'} title={i.copy ? t('Non si trasloca') : t('Così non si trasloca')}>
            <ul style={{ margin: 0, paddingInlineStart: '1.2em' }}>
              {blocchi.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </Banner>
        )}
        {repos.length + cartelle.length > 0 && (
          <List>
            {repos.map((r) => (
              <ListItem
                key={r.url + r.subdir}
                headline={<span className="md-mono">{r.url}</span>}
                supporting={`${t('ramo {ramo}', { ramo: r.branch || t('(nessuno: commit staccato)') })}${r.subdir ? ` · ${t('cartella {cartella}', { cartella: r.subdir })}` : ''} · ${moduliInstallati((r.modules ?? []).length)}`}
              />
            ))}
            {cartelle.map((c) => (
              <ListItem
                key={c.dir}
                headline={<span className="md-mono">{nomeDellaCartella(c.dir)}</span>}
                supporting={`${t('senza git: nel trasloco dici da quale repository viene')} · ${moduliInstallati((c.modules ?? []).length)}: ${(c.modules ?? []).join(', ')}`}
              />
            ))}
          </List>
        )}
        {librerie.length > 0 && (
          <p className="md-body-small" style={{ margin: 0 }}>
            {t('Librerie Python in più: {librerie}', { librerie: <span className="md-mono">{librerie.join(' ')}</span> })}
          </p>
        )}
        {!i.copy && (
          <div className="md-stack__inline">
            {aperto ? (
              <>
                {aperto.phase === 'done' && <StatusChip status="done" label={t('Traslocata in {nome}', { nome: aperto.name })} tone="success" />}
                <Button variant={aperto.phase === 'done' ? 'text' : 'tonal'} onClick={() => apri(aperto.id)}>{t('Apri il trasloco')}</Button>
              </>
            ) : (
              <Button variant="tonal" disabled={blocchi.length > 0} onClick={trasloca}>{t('Trasloca…')}</Button>
            )}
          </div>
        )}
        <Details summary={t('Dettagli tecnici')}>
          <p className="md-body-small md-mono" style={{ margin: 0 }}>
            {i.unit} · db {i.db} · {t('porta {porta}', { porta: i.httpPort })} · {i.dataDir}
          </p>
          {(i.warnings ?? []).length > 0 && (
            <p className="md-body-small" style={{ margin: 0 }}>{(i.warnings ?? []).join(' · ')}</p>
          )}
        </Details>
      </div>
    </Card>
  );
}

/**
 * Aprire un trasloco: dove va l'istanza e come si chiamerà. Non tocca
 * niente: repository, prova e trasloco vero sono passi della sua pagina.
 */
function NuovoTrasloco({ slug, sorgente, istanza, chiudi }: {
  slug: string; sorgente: string; istanza: IstanzaSorgente | null; chiudi: () => void;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const server = useServers(slug);
  const credenziali = useCredenzialiCloud(slug);
  const pronti = (server.data?.servers ?? []).filter((s) => s.status === 'ready');
  // Come nella continuità: un server pronto, o uno nuovo su OVH se
  // l'organizzazione ha un progetto collegato. Il trasloco lo compra dopo i
  // suoi controlli, e intanto che si installa si incollano le chiavi.
  const puoCreare = (credenziali.data?.credentials ?? []).some((c) => c.provider === 'ovh');
  const NUOVO = '__nuovo__';
  const [scelta, setScelta] = useState('');
  const dove = scelta === NUOVO && puoCreare ? NUOVO
    : scelta && pronti.some((s) => s.id === scelta) ? scelta
    : pronti[0]?.id ?? (puoCreare ? NUOVO : '');
  const [nomeServer, setNomeServer] = useState('');
  const [macchina, setMacchina] = useState<MacchinaCloud>(MACCHINA_VUOTA);
  const [nome, setNome] = useState('');
  const crea = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/migrations', {
        params: { path: { slug } },
        body: {
          sourceId: sorgente, sourceInstance: istanza!.name,
          ...(dove === NUOVO ? { newServer: { name: nomeServer, ...macchina } } : { serverId: dove }),
          ...(nome.trim() ? { name: nome.trim() } : {}),
        },
      })),
    onSuccess: (tr) => {
      void qc.invalidateQueries({ queryKey: ['migrations', slug] });
      void qc.invalidateQueries({ queryKey: ['servers', slug] });
      chiudi();
      navigate({ to: '/orgs/$slug/traslochi/$id', params: { slug, id: tr.id } });
    },
  });
  const nuovoOk = dove !== NUOVO || (/^[a-z0-9][a-z0-9-]{1,62}$/.test(nomeServer) && !!macchina.credentialId && !!macchina.region && !!macchina.size);
  return (
    <Dialog
      open={!!istanza}
      onOpenChange={(v) => { if (!v) { chiudi(); crea.reset(); } }}
      title={t('Trasloca {nome}', { nome: istanza?.name ?? '' })}
      description={t('Si apre il trasloco: niente si spegne e niente si copia finché non lo decidi tu, passo per passo.')}
      actions={
        <>
          <Button variant="text" onClick={chiudi}>{t('Annulla')}</Button>
          <Button variant="filled" icon={crea.isPending ? <Anello /> : undefined} disabled={crea.isPending || !dove || !nuovoOk} onClick={() => crea.mutate()}>
            {dove === NUOVO ? t('Crea il server e apri il trasloco') : t('Apri il trasloco')}
          </Button>
        </>
      }
    >
      <div className="md-stack">
        {!dove ? (
          <Banner title={t('Nessun server pronto')}>
            {t('Prima crea o collega il server su cui portarla, dalla pagina dei server. Con un progetto OVH collegato in Impostazioni, lo crei da qui.')}
          </Banner>
        ) : (
          <Select
            label={t('Server di destinazione')}
            value={dove}
            onChange={(e) => setScelta(e.target.value)}
            options={[
              ...pronti.map((s) => ({ value: s.id, label: `${s.name}${s.failoverIpv4 ? ` · ${s.failoverIpv4}` : s.ipv4 ? ` · ${s.ipv4}` : ''}` })),
              ...(puoCreare ? [{ value: NUOVO, label: t('Un server nuovo su OVH, creato adesso') }] : []),
            ]}
            help={dove === NUOVO
              ? t('Molo compra la macchina e la installa (circa dieci minuti). Intanto il trasloco si prepara: incolli le chiavi su GitHub, e la prova parte quando il server è pronto.')
              : undefined}
          />
        )}
        {dove === NUOVO && (
          <>
            <TextField
              label={t('Nome del server nuovo')}
              value={nomeServer}
              onChange={(e) => setNomeServer(e.target.value.toLowerCase())}
              placeholder="srv-clienti"
              help={t('Solo minuscole, cifre e trattini. È anche il nome della macchina su OVH.')}
            />
            <ScegliMacchina slug={slug} value={macchina} onChange={setMacchina} />
          </>
        )}
        <TextField
          label={t('Nome su Molo')}
          value={nome}
          onChange={(e) => setNome(e.target.value.toLowerCase())}
          placeholder={istanza ? istanza.name.toLowerCase().replace(/[^a-z0-9_-]/g, '-') : ''}
          help={t('Vuoto: il nome che ha su CloudPepper.')}
        />
        {crea.error && <Banner tone="error" title={t('Non l’ho aperto')}>{(crea.error as Error).message}</Banner>}
      </div>
    </Dialog>
  );
}
